import queryString from 'query-string';

import { sendAuthRequest } from 'src/services/api';
import {
  buildZendeskSignInUrl,
  ZENDESK_DASHBOARD_PATH,
} from 'src/utils/assistAuthUtils';
import { Routes } from 'src/utils/enums';

interface LoginDashboardRedirectRequest {
  email: string;
  turnstile_token: string;
  subdomain?: string;
}

export async function getDashboardRedirect(
  request: LoginDashboardRedirectRequest,
): Promise<string> {
  const response = await fetch(API_URL + 'security/dashboard/redirect', {
    body: JSON.stringify(request),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  if (!response.ok) {
    throw response;
  }

  const data = await response.json();

  return data.redirect_url;
}

/**
 * If `redirectUrl` points at a Zendesk-provisioned tenant (hostname
 * `<sub>.${ZENDESK_DOMAIN}`), wraps it into a Zendesk sign-in URL whose
 * `return_to` lands on the tenant's `/hc-redirect` route with the
 * original helpcenter `return_to` preserved. Returns `null` for non-ZD
 * destinations so the caller can fall back to the legacy flow.
 */
export function buildZendeskHelpCenterSignInUrl(
  redirectUrl: string,
): string | null {
  let url: URL;
  try {
    url = new URL(redirectUrl);
  } catch {
    return null;
  }

  const apexSuffix = `.${ZENDESK_DOMAIN}`;
  if (!url.hostname.endsWith(apexSuffix)) {
    return null;
  }
  const subdomain = url.hostname.slice(0, -apexSuffix.length);
  if (!subdomain || subdomain.includes('.')) {
    return null;
  }

  const originalReturnTo = getReturnToUrlParam();
  const hcRedirectUrl = new URL(
    `${url.protocol}//${url.hostname}${ZENDESK_DASHBOARD_PATH}/hc-redirect`,
  );
  if (originalReturnTo) {
    hcRedirectUrl.searchParams.set('return_to', originalReturnTo);
  }

  return buildZendeskSignInUrl(subdomain, hcRedirectUrl.toString());
}

function getReturnToUrlParam() {
  const searchParams = queryString.parse(window.location.search);
  return searchParams.return_to ? String(searchParams.return_to) : '';
}

export async function getHelpCenterRedirectUrl(): Promise<string> {
  const response = await sendAuthRequest(
    API_URL + 'security/auth/knowledge',
    null,
    'GET',
  );

  if ('error' in response) {
    throw new Error(response.error);
  }

  const data = await response.json();
  const url = new URL(data.redirect_url);
  const returnTo = getReturnToUrlParam();
  if (returnTo) {
    url.searchParams.set('return_to', returnTo);
  }

  return url.toString();
}

/**
 * - maintain existing URL parameters when redirecting to the dashboard login
 * - add email as a hash parameter to prefill the login form
 */
export function mergeURLParams(redirectUrl: string, email: string) {
  const existingHashParams = queryString.parse(window.location.hash);
  const existingSearchParams = queryString.parse(window.location.search);

  const allHashParams = {
    ...existingHashParams,
    username: email,
  };

  const url = new URL(redirectUrl);

  Object.entries(existingSearchParams).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, String(value));
    }
  });

  url.hash = queryString.stringify(allHashParams);

  return url.toString();
}

export function shouldRedirectToHelpCenter() {
  if (window.location.pathname.endsWith(Routes.HC_REDIRECT)) {
    return false;
  }
  const returnTo = getReturnToUrlParam();
  return !!returnTo;
}
